import {
  AlignStack,
  Button,
  colors,
  darkThemeSelector,
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuPopover,
  DropdownMenuTrigger,
  FocusRing,
  Icon,
  Small,
  styled,
} from '@meterup/atto';
import { selectors } from '@meterup/atto/src/controls/shared/styles';

const TableFiltersTokenHandleLabel = styled(Small, {
  fontWeight: '$bold',
  whiteSpace: 'nowrap',
});
TableFiltersTokenHandleLabel.displayName = 'TableFiltersTokenHandleLabel';

const TableFiltersTokenHandle = styled(AlignStack, {
  position: 'relative',
  padding: '$2 $6',
});
TableFiltersTokenHandle.displayName = 'TableFiltersTokenHandle';

const TableFiltersTokenValueLabel = styled(Small, {
  fontWeight: '$bold',
});
TableFiltersTokenValueLabel.displayName = 'TableFiltersTokenValueLabel';

const TableFiltersTokenValueArrows = styled(Icon, {
  width: '$10',
  height: '$10',
});
TableFiltersTokenValueArrows.displayName = 'TableFiltersTokenValueArrows';

const TableFiltersTokenValue = styled('button', FocusRing, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  padding: '$2 $6',
  borderTopRightRadius: '$8',
  borderBottomRightRadius: '$8',

  [selectors.focus]: {
    borderRadius: '$8',
  },

  variants: {
    modified: {
      true: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },

        [`& ${TableFiltersTokenValueLabel}`]: {
          color: colors.bodyBrandLight,

          [darkThemeSelector]: {
            color: colors.bodyBrandDark,
          },
        },

        [`& ${TableFiltersTokenValueArrows}`]: {
          color: colors.iconBrandLight,

          [darkThemeSelector]: {
            color: colors.iconBrandDark,
          },
        },

        [selectors.hover]: {
          [`& ${TableFiltersTokenValueLabel}`]: {
            color: colors.headingBrandLight,

            [darkThemeSelector]: {
              color: colors.headingBrandDark,
            },
          },

          [`& ${TableFiltersTokenValueArrows}`]: {
            color: colors.bodyBrandLight,

            [darkThemeSelector]: {
              color: colors.bodyBrandDark,
            },
          },
        },
      },
      false: {
        background: colors.bgNeutralLight,
        strokeAll: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: colors.strokeNeutralDark,
        },

        [`& ${TableFiltersTokenValueLabel}`]: {
          color: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            color: colors.bodyNeutralDark,
          },
        },

        [`& ${TableFiltersTokenValueArrows}`]: {
          color: colors.iconNeutralLight,

          [darkThemeSelector]: {
            color: colors.iconNeutralDark,
          },
        },

        [selectors.hover]: {
          [`& ${TableFiltersTokenValueLabel}`]: {
            color: colors.headingNeutralLight,

            [darkThemeSelector]: {
              color: colors.headingNeutralDark,
            },
          },

          [`& ${TableFiltersTokenValueArrows}`]: {
            color: colors.bodyNeutralLight,

            [darkThemeSelector]: {
              color: colors.bodyNeutralDark,
            },
          },
        },
      },
    },
  },
});
TableFiltersTokenValue.displayName = 'TableFiltersTokenValue';

const TableFiltersTokenTrigger = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  minHeight: '$24',
  strokeAll: colors.strokeNeutralLight,
  borderRadius: '$8',

  [darkThemeSelector]: {
    strokeAll: colors.strokeNeutralDark,
  },
});
TableFiltersTokenTrigger.displayName = 'TableFiltersTokenTrigger';

const TableFiltersTokenHeading = styled(AlignStack, {
  padding: '$2 $10',
});
TableFiltersTokenHeading.displayName = 'TableFiltersTokenHeading';

const TableFiltersTokenContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
});
TableFiltersTokenContainer.displayName = 'TableFiltersTokenContainer';

export function TableFiltersToken({
  id,
  changeable,
  dismissable = true,
  handle,
  modified = true,
  value,
  children,
  onTokenClear,
  isOpen,
  onOpenChange,
  ...restProps
}: {
  id: string;
  changeable?: boolean;
  dismissable?: boolean;
  handle?: React.ReactNode;
  modified?: boolean;
  value?: React.ReactNode;
  children?: React.ReactNode;
  onTokenClear: React.MouseEventHandler<HTMLButtonElement>;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) {
  return (
    <TableFiltersTokenContainer {...restProps}>
      <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
        <TableFiltersTokenTrigger>
          <TableFiltersTokenHandle align="center" width="min-content" preset="small">
            {handle && <TableFiltersTokenHandleLabel>{handle}</TableFiltersTokenHandleLabel>}
          </TableFiltersTokenHandle>
          <DropdownMenuTrigger asChild>
            <TableFiltersTokenValue
              tabIndex={0}
              className={`disableOutsideClick_${id}`}
              modified={modified}
            >
              <TableFiltersTokenValueLabel whitespace="no-wrap">
                {value ?? 'All'}
              </TableFiltersTokenValueLabel>
              {changeable && <TableFiltersTokenValueArrows icon="chevrons-vertical" />}
            </TableFiltersTokenValue>
          </DropdownMenuTrigger>
        </TableFiltersTokenTrigger>
        <DropdownMenuPopover
          align="start"
          side="bottom"
          sideOffset={8}
          usePortal={false}
          // Disable outside click detection to prevent page from becoming unclickable
          onPointerDownOutside={() => {}}
          onInteractOutside={() => {}}
        >
          {dismissable && (
            <DropdownMenuGroup>
              <TableFiltersTokenHeading
                align="center"
                gap={6}
                preset="small"
                width="100%"
                end={
                  <Button
                    onClick={onTokenClear}
                    arrangement="hidden-label"
                    appearance="muted"
                    icon="trash-can"
                    ghost
                    size="x-small"
                    variant="destructive"
                  >
                    Clear filter
                  </Button>
                }
              >
                <Small weight="bold">{handle}</Small>
              </TableFiltersTokenHeading>
            </DropdownMenuGroup>
          )}
          <DropdownMenuGroup scrollable>{children}</DropdownMenuGroup>
        </DropdownMenuPopover>
      </DropdownMenu>
    </TableFiltersTokenContainer>
  );
}
